import React, { useState, useEffect } from 'react';
import { 
  Calendar, Search, Loader2, X, Eye, MapPin, Clock, ArrowLeft,
  Sparkles, BookOpen, Music, Trophy, Heart, MoreHorizontal
} from 'lucide-react';
import { studentEventsAPI } from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const CATEGORY_CONFIG = {
  spiritual: { label: "Ma'naviy tadbir", icon: Sparkles, color: '#8b5cf6', bg: '#ede9fe' },
  educational: { label: "Ma'rifiy tadbir", icon: BookOpen, color: '#3b82f6', bg: '#dbeafe' },
  cultural: { label: "Madaniy tadbir", icon: Music, color: '#ec4899', bg: '#fce7f3' },
  sport: { label: "Sport tadbiri", icon: Trophy, color: '#22c55e', bg: '#dcfce7' },
  volunteer: { label: "Volontyorlik", icon: Heart, color: '#f59e0b', bg: '#fef3c7' },
  other: { label: "Boshqa", icon: MoreHorizontal, color: '#64748b', bg: '#f1f5f9' },
};

const StudentEvents = () => {
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, [filterCategory]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      
      const response = await studentEventsAPI.getAll(params);
      setEvents(response.data.events || []);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    return `${API_URL}${image}`;
  };

  const openEventDetail = async (eventId) => {
    setDetailLoading(true);
    try {
      const response = await studentEventsAPI.get(eventId);
      setSelectedEvent(response.data.event);
    } catch (error) {
      console.error('Xato:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const filteredEvents = events.filter(e =>
    e.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Detail view
  if (selectedEvent) {
    const catConfig = CATEGORY_CONFIG[selectedEvent.category] || CATEGORY_CONFIG.other;
    const CatIcon = catConfig.icon;
    
    return (
      <div className="page-container">
        <button className="btn btn-back" onClick={() => setSelectedEvent(null)}>
          <ArrowLeft size={18} />
          Orqaga
        </button>
        
        <div className="event-detail-page">
          {selectedEvent.image && (
            <div className="event-detail-image">
              <img src={getImageUrl(selectedEvent.image)} alt={selectedEvent.title} />
            </div>
          )}
          
          <div className="event-detail-header">
            <div className="event-category-badge" style={{ background: catConfig.bg, color: catConfig.color }}>
              <CatIcon size={16} />
              {catConfig.label}
            </div>
            <h1>{selectedEvent.title}</h1>
            <div className="event-detail-meta">
              {selectedEvent.event_date && (
                <span><Clock size={16} /> {new Date(selectedEvent.event_date).toLocaleDateString('uz-UZ', { 
                  year: 'numeric', month: 'long', day: 'numeric' 
                })}</span>
              )}
              {selectedEvent.location && (
                <span><MapPin size={16} /> {selectedEvent.location}</span>
              )}
              <span><Eye size={16} /> {selectedEvent.views_count} marta ko'rildi</span>
            </div>
          </div>
          
          <div className="event-detail-content">
            {selectedEvent.content.split('\n').map((paragraph, idx) => (
              <p key={idx}>{paragraph}</p>
            ))}
          </div>
          
          <div className="event-detail-footer">
            <span>Joylangan: {new Date(selectedEvent.created_at).toLocaleDateString('uz-UZ')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><Calendar size={28} /> Ma'naviy-ma'rifiy tadbirlar</h1>
          <p>Texnikumda o'tkazilgan tadbirlar haqida yangiliklar</p>
        </div>
      </header>

      {/* Category Filter Pills */}
      <div className="category-pills">
        <button 
          className={`category-pill ${filterCategory === '' ? 'active' : ''}`}
          onClick={() => setFilterCategory('')}
        >
          Barchasi
        </button>
        {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
          const Icon = config.icon;
          return (
            <button 
              key={key}
              className={`category-pill ${filterCategory === key ? 'active' : ''}`}
              style={{ '--pill-color': config.color, '--pill-bg': config.bg }}
              onClick={() => setFilterCategory(key)}
            >
              <Icon size={16} />
              {config.label}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="filters-row">
        <div className="search-bar">
          <Search size={20} />
          <input 
            type="text" 
            placeholder="Tadbir qidirish..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Events Grid */}
      {loading ? (
        <div className="loading-container">
          <Loader2 className="spinner" size={40} />
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="empty-state">
          <Calendar size={48} />
          <p>Tadbirlar topilmadi</p>
        </div>
      ) : (
        <div className="student-events-grid">
          {filteredEvents.map(event => {
            const catConfig = CATEGORY_CONFIG[event.category] || CATEGORY_CONFIG.other;
            const CatIcon = catConfig.icon;
            return (
              <div 
                key={event.id} 
                className="student-event-card"
                onClick={() => openEventDetail(event.id)}
              >
                <div className="event-card-image">
                  {event.image ? (
                    <img src={getImageUrl(event.image)} alt={event.title} />
                  ) : (
                    <div className="no-image" style={{ background: catConfig.bg }}>
                      <CatIcon size={48} style={{ color: catConfig.color }} />
                    </div>
                  )}
                  <div className="event-category-tag" style={{ background: catConfig.color }}>
                    <CatIcon size={12} />
                    {catConfig.label}
                  </div>
                </div>
                <div className="event-card-body">
                  <h3>{event.title}</h3>
                  <div className="event-card-meta">
                    {event.event_date && (
                      <span><Clock size={14} /> {new Date(event.event_date).toLocaleDateString('uz-UZ')}</span>
                    )}
                    {event.location && (
                      <span><MapPin size={14} /> {event.location}</span>
                    )}
                  </div>
                  <div className="event-card-footer">
                    <span className="views"><Eye size={14} /> {event.views_count}</span>
                    <span className="date">{new Date(event.created_at).toLocaleDateString('uz-UZ')}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Loading overlay for detail */}
      {detailLoading && (
        <div className="modal-overlay">
          <Loader2 className="spinner" size={40} />
        </div>
      )}
    </div>
  );
};

export default StudentEvents;
